import { useState } from 'react'

import type { Form } from 'factorial-form'
import {
  DatePickerValue,
  F0Box,
  F0Checkbox,
  F0DatePicker,
  F0Dialog,
  F0Select,
} from '@factorialco/f0-react'
import { Input } from '@factorialco/f0-react/dist/experimental'
import { Features } from 'generated/featureDefinitions'

import {
  ProjectForSelectFragment,
  ProjectManagementExportModalDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import { F0ProjectSelector } from 'modules/project_management/components/selectors/F0ProjectSelector'
import useProjectsExport from 'modules/project_management/hooks/useProjectsExport'
import {
  dateRangeOptions,
  exportTypeOptions,
  fileFormatOptions,
  timeFormatOptions,
} from 'modules/project_management/lib/exportFormOptions'

import { FormValues } from './types'

function uiDateStringToDatePickerValue(
  dateString: string | undefined
): DatePickerValue | undefined {
  if (!dateString) return undefined
  const parsed = stringToDate(dateString, 'ui')
  if (!parsed.isValid()) return undefined
  const d = parsed.toDate()
  return { granularity: 'day', value: { from: d, to: d } }
}

function datePickerValueToUiString(value: DatePickerValue | undefined): string | undefined {
  const from = value?.value?.from
  if (!from) return undefined
  return i18nDateToString(from, 'ui')
}

export interface ExportProjectsModalProps {
  onClose: () => void
  canSeeCost: boolean
  exportForm: Form
  project?: ProjectForSelectFragment
  hasTimeTrackingSection: boolean
  hasFlexibleTracking: boolean
}

const ExportProjectsModal = ({
  onClose,
  canSeeCost,
  exportForm,
  project,
  hasTimeTrackingSection,
  hasFlexibleTracking,
}: ExportProjectsModalProps) => {
  const [selectedProjects, setSelectedProjects] = useState<ProjectForSelectFragment[]>(
    compact([project])
  )
  const [_employeeSelection, setEmployeeSelection] = useState<EmployeeSelectorV2MultiSelectState>({
    type: 'manual',
    ids: [],
  })
  const [includeEmployeesWithoutTimeTracked, setIncludeEmployeesWithoutTimeTracked] =
    useState<boolean>(false)

  const [includeAllProjects, setIncludeAllProjects] = useState<boolean>(false)

  const { company } = useCurrentSession()

  const { data: policyData } = useQuery(ProjectManagementExportModalDocument, {
    variables: {
      companyId: company.id,
      featureIds: [
        Features.projects_clients,
        Features.projects_with_subprojects,
        Features.projects_budget,
      ],
    },
  })

  const hasClientsFeature = !!policyData?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_clients
  )?.available

  const hasSubprojectsFeature = !!policyData?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_with_subprojects
  )?.available

  const hasProjectsBudgetFeature =
    !!policyData?.companies.featureAvailabilitiesConnection.nodes.find(
      (feature) => feature.id === Features.projects_budget
    )?.available

  const showFinancials = hasProjectsBudgetFeature && canSeeCost

  const canManageProjectTasks =
    policyData?.projectManagement._policies.projectTaskManage.can ?? false

  const exportTimeAndCostByMonthsFFenabled =
    policyData?.features.featuresConnection.nodes[0]?.enabled ?? false

  const handleEmployeeChange = (newSelection: EmployeeSelectorV2MultiSelectState) => {
    setEmployeeSelection(newSelection)
    exportForm.get('employee_selection').set(newSelection)
  }

  const onProjectChange = (projects: ProjectForSelectFragment[]) => {
    setSelectedProjects(projects)
    exportForm.get('project_ids').set(projects.map((p) => p.id))
  }

  const onIncludeEmployeesWithoutTimeTrackedChange = (value: boolean) => {
    setIncludeEmployeesWithoutTimeTracked(value)
    exportForm.get('include_employees_without_time_tracked').set(value)
  }

  const onAllProjectsChange = (value: boolean) => {
    setIncludeAllProjects(value)
    exportForm.get('all_projects').set(value)

    if (value === true) {
      onProjectChange([])
    }
  }

  const {
    name,
    export_type,
    file_format,
    time_format,
    project_ids,
    employee_selection,
    date_range,
    from_date,
    to_date,
  } = exportForm.data()

  const { onExport } = useProjectsExport({
    formValues: exportForm.values,
    hasClientsFeature,
    hasSubprojectsFeature,
    showFinancials,
  })

  const fromMaxDate = () => {
    return !exportForm.get('to_date').value
      ? moment().endOf('day')
      : moment(stringToDate(exportForm.get('to_date').value, 'ui')).endOf('day')
  }

  const mandatoryFieldsCompleted = () => {
    let mandatory = false
    let optional = true

    if (isSubprojectsType()) {
      mandatory = !!project_ids.length || includeAllProjects
      return !!name && mandatory
    }

    if (isShiftsType() || isEmployeeType()) {
      mandatory =
        (employee_selection?.type === 'manual' && !!employee_selection?.ids.length) ||
        employee_selection?.type === 'all'
    } else {
      mandatory = !!project_ids.length || includeAllProjects
    }

    if (date_range === 'custom_range') {
      optional = !!from_date && !!to_date
    }
    if (file_format === 'xlsx' && !isExpensesType()) {
      optional = optional && !!time_format
    }

    return !!name && mandatory && optional
  }

  if (file_format !== 'xlsx') exportForm.get('time_format').value = ''
  if (date_range !== 'custom_range') {
    exportForm.get('from_date').value = ''
    exportForm.get('to_date').value = ''
  }

  const trackAmplitude = (formValues: FormValues) => {
    track(trackingEvents.projectManagement.projects.downloadProjectsReportSubmit, {
      export_type: exportTypeOptions().find((element) => element.value === formValues.export_type)
        ?.value,
      num_projects_selected: formValues.project_ids?.length,
      num_employees_selected: formValues.employee_ids?.length,
      data_range: formValues.date_range,
      format_option: formValues.file_format,
      time_format: formValues.time_format === 'hours' ? 'decimals' : formValues.time_format,
      include_employees_without_time_tracked: formValues.include_employees_without_time_tracked,
      all_projects: formValues.all_projects,
    })
  }

  const submitModal = () => {
    onExport()
    trackAmplitude(exportForm.values)
    onClose()
  }

  const isTotalTimeType = () => {
    return export_type === 'total_time'
  }

  const isShiftsType = () => {
    return export_type === 'shifts'
  }

  const isEmployeeType = () => {
    return export_type === 'total_time_by_employee'
  }

  const isExpensesType = () => {
    return export_type === 'expenses'
  }

  const isSubprojectsType = () => {
    return export_type === 'subprojects'
  }

  const filteredExportTypeOptions = () => {
    const options = []

    if (hasTimeTrackingSection || hasFlexibleTracking) {
      const totalTimeOption = exportTypeOptions().find((element) => element.value === 'total_time')
      const totalTimeByDayOption = exportTypeOptions().find(
        (element) => element.value === 'total_time_by_date'
      )
      const totalTimeAndCostByMonthOption = exportTypeOptions().find(
        (element) => element.value === 'total_time_and_cost_by_month'
      )
      const employeesOption = exportTypeOptions().find(
        (element) => element.value === 'total_time_by_employee'
      )
      const shiftsOption = exportTypeOptions().find((element) => element.value === 'shifts')
      if (totalTimeOption !== undefined) options.push(totalTimeOption)
      if (totalTimeByDayOption !== undefined) options.push(totalTimeByDayOption)
      if (
        exportTimeAndCostByMonthsFFenabled &&
        canSeeCost &&
        totalTimeAndCostByMonthOption !== undefined
      )
        options.push(totalTimeAndCostByMonthOption)
      if (shiftsOption !== undefined) options.push(shiftsOption)
      if (employeesOption !== undefined) options.push(employeesOption)
    }

    const expensesOption = exportTypeOptions().find((element) => element.value === 'expenses')
    if (expensesOption !== undefined && canSeeCost) options.push(expensesOption)

    if (canManageProjectTasks) {
      const tasksOption = exportTypeOptions().find((element) => element.value === 'tasks')
      if (tasksOption !== undefined) options.push(tasksOption)
    }

    if (hasSubprojectsFeature) {
      const subprojectsOption = exportTypeOptions().find(
        (element) => element.value === 'subprojects'
      )
      if (subprojectsOption !== undefined) options.push(subprojectsOption)
    }

    return options
  }

  return (
    <F0Dialog
      title={i18n.t('project_management.modals.export_projects_data.title')}
      description={i18n.t('project_management.modals.export_projects_data.description')}
      onClose={onClose}
      isOpen
      disableContentPadding
      width="sm"
      primaryAction={{
        label: i18n.t('project_management.modals.export_projects_data.submit'),
        onClick: submitModal,
        disabled: !mandatoryFieldsCompleted(),
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <F0Box display="flex" flexDirection="column" gap="xl" padding="xl">
        <Input
          type="text"
          size="md"
          label={i18n.t('project_management.modals.export_projects_data.input_label.file_name')}
          value={exportForm.get('name').value ?? ''}
          onChange={(value) => exportForm.get('name').set(value)}
          required
          error={!exportForm.get('name').isDirty ? exportForm.get('name').errors?.[0] : undefined}
        />
        <F0Select
          size="md"
          label={i18n.t('project_management.modals.export_projects_data.input_label.export_type')}
          options={filteredExportTypeOptions()}
          value={
            exportForm.get('export_type').value === ''
              ? undefined
              : exportForm.get('export_type').value
          }
          onChange={(value: string) => exportForm.get('export_type').set(value)}
          disabled={filteredExportTypeOptions().length === 1}
          error={
            !exportForm.get('export_type').isDirty
              ? exportForm.get('export_type').errors?.[0]
              : undefined
          }
        />
        <F0Select
          size="md"
          label={i18n.t('project_management.modals.export_projects_data.input_label.file_format')}
          options={fileFormatOptions}
          value={
            exportForm.get('file_format').value === ''
              ? undefined
              : exportForm.get('file_format').value
          }
          onChange={(value: string) => exportForm.get('file_format').set(value)}
          error={
            !exportForm.get('file_format').isDirty
              ? exportForm.get('file_format').errors?.[0]
              : undefined
          }
        />
        {file_format === 'xlsx' && !isExpensesType() && !isSubprojectsType() && (
          <F0Select
            size="md"
            label={i18n.t('project_management.modals.export_projects_data.input_label.time_format')}
            options={timeFormatOptions()}
            value={
              exportForm.get('time_format').value === ''
                ? undefined
                : exportForm.get('time_format').value
            }
            onChange={(value: string) => exportForm.get('time_format').set(value)}
            placeholder={i18n.t('common.select')}
            error={
              !exportForm.get('time_format').isDirty
                ? exportForm.get('time_format').errors?.[0]
                : undefined
            }
          />
        )}
        {!isShiftsType() && !isEmployeeType() && (
          <>
            <F0ProjectSelector
              mandatory={!includeAllProjects}
              disabled={!!project || includeAllProjects}
              value={selectedProjects}
              onChange={onProjectChange}
            />
            {!project && (
              <F0Checkbox
                checked={includeAllProjects}
                title={i18n.t(
                  'project_management.modals.export_projects_data.input_label.all_projects'
                )}
                onCheckedChange={onAllProjectsChange}
              />
            )}
          </>
        )}
        {(isShiftsType() || isEmployeeType()) && (
          <EmployeeSelectorV2
            label={i18n.t(
              'project_management.modals.export_projects_data.input_label.select_employee'
            )}
            multiple
            onChange={handleEmployeeChange}
          />
        )}
        {!isSubprojectsType() && (
          <F0Select
            size="md"
            label={i18n.t('project_management.modals.export_projects_data.input_label.date_range')}
            options={dateRangeOptions()}
            value={
              exportForm.get('date_range').value === ''
                ? undefined
                : exportForm.get('date_range').value
            }
            onChange={(value: string) => exportForm.get('date_range').set(value)}
            error={
              !exportForm.get('date_range').isDirty
                ? exportForm.get('date_range').errors?.[0]
                : undefined
            }
          />
        )}
        {!isSubprojectsType() && date_range === 'custom_range' && (
          <F0Box display="flex" flexDirection="row" gap="xl">
            <F0DatePicker
              required
              size="md"
              label={i18n.t('project_management.modals.export_projects_data.input_label.from')}
              value={uiDateStringToDatePickerValue(exportForm.get('from_date').value)}
              onChange={(value) => {
                exportForm.get('from_date').set(datePickerValueToUiString(value) ?? '')
              }}
              maxDate={fromMaxDate().toDate()}
              error={
                !exportForm.get('from_date').isDirty
                  ? exportForm.get('from_date').errors?.[0]
                  : undefined
              }
            />
            <F0DatePicker
              required
              size="md"
              label={i18n.t('project_management.modals.export_projects_data.input_label.to')}
              value={uiDateStringToDatePickerValue(exportForm.get('to_date').value)}
              onChange={(value) => {
                exportForm.get('to_date').set(datePickerValueToUiString(value) ?? '')
              }}
              minDate={
                from_date ? stringToDate(from_date, 'ui').startOf('day').toDate() : undefined
              }
              error={
                !exportForm.get('to_date').isDirty
                  ? exportForm.get('to_date').errors?.[0]
                  : undefined
              }
            />
          </F0Box>
        )}
        {isTotalTimeType() && (
          <F0Checkbox
            checked={includeEmployeesWithoutTimeTracked}
            title={i18n.t(
              'project_management.modals.export_projects_data.input_label.include_employees_without_time_tracked'
            )}
            onCheckedChange={onIncludeEmployeesWithoutTimeTrackedChange}
          />
        )}
      </F0Box>
    </F0Dialog>
  )
}

export default observer(ExportProjectsModal)
